import { Button, Select, Skeleton, Space } from 'antd';
import Page from 'components/page/page';
import PageSection from 'components/page/pageSection';
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUserId, useUserLevel } from 'reducers';
import { getReadableBdo, getReadablePaymentType, getReadablePriceType, PriceType, UserLevel } from 'types';
import { Formatting } from '../../utils';
import { CheckCircleOutlined, DeleteOutlined, InfoCircleOutlined, UndoOutlined } from '@ant-design/icons';
import moment from 'moment';
import { AsyncButton, PhoneLink } from 'components';
import { useNewTaskDetails } from './use-new-task-details';

export const NewTaskDetails: React.FC = () => {
	const navigate = useNavigate();
	const { id: idRaw } = useParams();
	const id = idRaw ? parseInt(idRaw, 10) : undefined;

	const [assignee, setAssignee] = React.useState<number>();
	const { task, order, client, assignedUser, drivers, assign, take, resign } = useNewTaskDetails(id);
	const userLevel = useUserLevel() ?? UserLevel.driver;
	const userId = useUserId();

	const getActions = () => {
		if (!task || task.doneAt) {
			return [];
		}

		if (!task.takenAt) {
			return [
				<AsyncButton key="0" icon={<CheckCircleOutlined />} onClick={() => take()}>
					Zgłoś się do realizacji
				</AsyncButton>,
			];
		}

		if (task.assignedTo === userId) {
			return [
				<Button
					key="0"
					type="primary"
					size="large"
					icon={<CheckCircleOutlined />}
					onClick={() => navigate(`/panel/v2/tasks/${task.id}/close`)}
				>
					Zrealizuj zadanie
				</Button>,
				<AsyncButton key="1" danger icon={<UndoOutlined />} onClick={() => resign()}>
					Wycofaj z realizacji
				</AsyncButton>,
			];
		}

		return [];
	};

	return (
		<Page title="Terminarz / Szczegóły zadania" actions={getActions()}>
			<Skeleton active loading={!task || !order || !client} />
			{task && order && client && (
				<div className="taskDetails flex">
					<div className="flex flex-dir flex-1">
						<PageSection title="Zamówienie" width={854}>
							<table>
								<tbody>
									<tr>
										<td>
											<p>Adres</p>
											<p>
												<span className="user-data">{order.address}</span>
											</p>
										</td>
										<td>
											<p>Rodzaj odpadów</p>
											<p>
												<span className="user-data">{order.wasteType}</span>
											</p>
										</td>
									</tr>
									<tr>
										<td>
											<p>
												Cena (<span className="price-type">{getReadablePriceType(order.priceType)}</span>)
											</p>
											<p>
												<span className="user-data">
													{Formatting.money(order.priceType === PriceType.net ? order.netPrice : order.grossPrice)}
												</span>
											</p>
										</td>
										<td>
											<p>Rodzaj płatności</p>
											<p>
												<span className="user-data">{getReadablePaymentType(order.wantedPaymentType)}</span>
											</p>
										</td>
									</tr>
									<tr>
										<td>
											<p>Termin</p>
											<div className="date">
												<p>
													<span className="user-data">
														{Formatting.epochToDate(task.date, moment(task.date).hours() === 0)}
													</span>
												</p>
											</div>
										</td>
										<td className="button">
											<Button
												type="primary"
												size="large"
												icon={<InfoCircleOutlined />}
												onClick={() => navigate(`/panel/orders/${task.orderId}/${task.orderType}`)}
											>
												Szczegóły zamówienia
											</Button>
										</td>
									</tr>
								</tbody>
							</table>
							{task.takenAt && assignedUser && (
								<div className="table">
									<table>
										<tbody>
											<tr>
												<td>Zgłoszone do realizacji:</td>
												<td>
													<span className="creation-data">{Formatting.epochToDate(task.takenAt)}</span>
												</td>
											</tr>

											<tr>
												<td>Zrealizowane:</td>
												<td>
													<span className="creation-data">
														{task.doneAt ? Formatting.epochToDate(task.doneAt) : '-'}
													</span>
												</td>
											</tr>

											<tr>
												<td>Przez:</td>
												<td>
													{userLevel >= UserLevel.admin ? (
														<Link to={`/panel/drivers/${assignedUser.id}`}>
															{assignedUser.name} {assignedUser.surname}
														</Link>
													) : (
														<span>{assignedUser.name}</span>
													)}
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							)}
						</PageSection>

						<PageSection title="Klient" width={876}>
							<table>
								<tbody>
									<tr>
										<td>
											<p>Klient</p>
											<p>
												<span className="user-data">{client.name}</span>
											</p>
										</td>
										<td>
											<p>BDO</p>
											<p>
												<span className="user-data">{getReadableBdo(order.bdo)}</span>
											</p>
										</td>
									</tr>
									<tr>
										<td>
											<p>Typ</p>
											<p>
												<span className="user-data">{client.type === 0 ? 'Firma' : 'Osoba prywatna'}</span>
											</p>
										</td>
										<td>
											<p>Numer BDO</p>
											<p>
												<span className="user-data">{client.bdo ?? '-'}</span>
											</p>
										</td>
									</tr>
									<tr>
										<td>
											<p>Numer telefonu</p>
											<p>
												<span className="user-data">
													<PhoneLink phone={client.phone} />
												</span>
											</p>
										</td>
										<td>
											<p>Email</p>
											<p>
												<span className="user-data">{client.email}</span>
											</p>
										</td>
									</tr>
									<tr>
										<td>
											<p>Numer NIP</p>
											<p>
												<span className="user-data">{client.nip}</span>
											</p>
										</td>
										<td className="button">
											{userLevel >= UserLevel.admin && (
												<Button
													type="primary"
													size="large"
													icon={<InfoCircleOutlined />}
													onClick={() => navigate(`/panel/clients/${client.id}`)}
												>
													Szczegóły klienta
												</Button>
											)}
										</td>
									</tr>
								</tbody>
							</table>
						</PageSection>

						{userLevel >= UserLevel.admin && !task.takenAt && drivers && (
							<PageSection title="Przypisz zadanie kierowcy" width={876}>
								<div className="drivers flex flex-dir">
									<label>Kierowca</label>
									<Space size={25}>
										<Select size="large" value={task.assignedTo ?? assignee} onChange={(id) => setAssignee(id)}>
											{drivers.map((driver) => (
												<Select.Option key={driver.id} value={driver.id}>
													{driver.name} {driver.surname}
												</Select.Option>
											))}
										</Select>
										<AsyncButton
											icon={<CheckCircleOutlined />}
											onClick={() => assign(assignee!)}
											disabled={!assignee || assignee === task.assignedTo}
										>
											Przypisz
										</AsyncButton>
										<AsyncButton
											icon={<DeleteOutlined />}
											danger
											onClick={() => assign(null).then(() => setAssignee(undefined))}
											disabled={!task.assignedTo}
										>
											Anuluj przypisanie
										</AsyncButton>
									</Space>
								</div>
							</PageSection>
						)}
					</div>
				</div>
			)}

			{/* {order && (
						<Modal
							title="Realizacja zadania"
							open={showExecutionModal}
							onCancel={() => setShowExecutionModal(false)}
							footer={null}
						>
							<div style={{ padding: '24px' }}>
								Cena: {Formatting.money(order.price)} {getReadablePriceType(order.priceType)} (
								{order.priceType === PriceType.net && <span>{Formatting.money(order.price * 1.08)} brutto</span>})
								<Checkbox checked={paid} onChange={(e) => setPaid(e.target.checked)}>
									Zamówienie opłacone
								</Checkbox>
							</div>
							<Space direction="horizontal" size={8}>
								<Button size="large" onClick={() => setShowExecutionModal(false)}>
									Anuluj
								</Button>
								<AsyncButton onClick={() => execute(paid).then(() => setShowExecutionModal(false))}>
									Zrealizuj
								</AsyncButton>
							</Space>
						</Modal>
					)} */}
		</Page>
	);
};
